import BootstrapTable from 'react-bootstrap-table-next';
import { useSelector } from 'react-redux';
import { NO_VALUE_PLACEHOLDER } from '../../../constants/tables';
import { getOrderLinePrices, getOrderSummaryTableColumns, getTypesBySupplierIds } from './utils';

export function OrderSummaryTable() {
  const {
    retailer: { order_type: retailerOrderType },
    retailerSuppliers,
  } = useSelector((state) => state.role);

  const {
    order: { current: order },
  } = useSelector((state) => state.retailerOrders);
  const { type: orderType, order_lines: orderLines } = order;

  let currency;

  const typesBySupplierId = getTypesBySupplierIds({
    retailerSuppliers,
    retailerOrderType: retailerOrderType || orderType,
  });

  const supplierIdWithFulfillment = new Set();

  const getVariantsFromFulfillment = (fulfillment, variants = {}) => {
    const {
      fulfillment_lines: lines,
      fulfillment_returns: returns,
      fulfillment_shipments: shipments,
      supplier_id: supplierId,
    } = fulfillment;

    lines.forEach((fulfillmentLine) => {
      const { quantity, variant_id: variantId } = fulfillmentLine;

      const orderLine = orderLines.find((orderLine) => orderLine.variant_id === fulfillmentLine.variant_id);
      const types = typesBySupplierId[supplierId];
      supplierIdWithFulfillment.add(types.retailerSupplier.supplier.id);
      enrichFulfillmentLine(fulfillmentLine, orderLine, types);

      if (!variants.hasOwnProperty(variantId)) {
        variants[variantId] = fulfillmentLine;
      } else {
        variants[variantId].quantity += quantity;
      }

      if (!currency) {
        currency = fulfillmentLine.variant.currency;
      }
    });

    const returnLines = returns.flatMap((fulfillmentReturn) => fulfillmentReturn.fulfillment_return_lines);
    returnLines.forEach((returnLine) => {
      const line = lines.find(({ id }) => id === returnLine.fulfillment_line_id);
      if (!line) return;

      variants[line.variant_id].returned += returnLine.quantity;
    });

    shipments?.forEach((shipment) => {
      const carrier = shipment.shipping_method?.currier || fulfillment.shipping_method?.currier || NO_VALUE_PLACEHOLDER;
      shipment.fulfillment_shipment_lines.forEach((shipmentLine) => {
        variants[shipmentLine.variant_id].shipped += shipmentLine.quantity;
        variants[shipmentLine.variant_id].tracking_number = shipment.tracking_number || NO_VALUE_PLACEHOLDER;
        variants[shipmentLine.variant_id].carrier = carrier;
      });
    });

    return variants;
  };

  const getRowsFromFulfillments = () => {
    const variants = {};
    order.fulfillments?.forEach((fulfillment) => getVariantsFromFulfillment(fulfillment, variants));
    return Object.values(variants);
  };

  const rows = order ? getRowsFromFulfillments() : [];
  const shouldAddCommissionColumn = Array.from(supplierIdWithFulfillment).some(
    (supplierId) => typesBySupplierId[supplierId].isMarketplaceRetailer,
  );
  const columns = getOrderSummaryTableColumns(rows, currency, shouldAddCommissionColumn);

  return (
    <BootstrapTable
      data={rows}
      columns={columns}
      keyField="id"
      striped
      bordered={false}
      hover
      noDataIndication={() => `There are no products to display`}
    />
  );
}
function enrichFulfillmentLine(fulfillmentLine, orderLine, types) {
  const { commission, retailPrice, cost, lineTotal } = getOrderLinePrices(orderLine, types);

  fulfillmentLine.supplier_name = types.retailerSupplier.supplier.name;
  fulfillmentLine.retail_price = retailPrice;
  fulfillmentLine.cost = cost;
  fulfillmentLine.shipped = 0;
  fulfillmentLine.cancelled = fulfillmentLine.original_quantity - fulfillmentLine.quantity;
  fulfillmentLine.returned = 0;
  fulfillmentLine.tracking_number = NO_VALUE_PLACEHOLDER;
  fulfillmentLine.carrier = NO_VALUE_PLACEHOLDER;
  fulfillmentLine.commission = commission;
  fulfillmentLine.line_total = lineTotal;
  return fulfillmentLine;
}
